import { matchIframe, matchScript, type Tracker, type TrackerId } from './trackers';

/**
 * Automatic detection and blocking of known trackers (ADR-018).
 *
 * Two hooks, both installed synchronously when Kockica loads (it must be the first script in <head>):
 * 1. A MutationObserver on the whole document catches <script src> and <iframe src> that the HTML parser or other code
 *    inserts. A blocked script gets type="text/plain" before the browser runs it; a blocked iframe loses its src.
 * 2. A wrapper around document.createElement('script') catches scripts that tag snippets build in JavaScript
 *    (GTM, Meta Pixel, Hotjar …): setting a matching src also sets type="text/plain".
 * On consent, blocked scripts are re-created as real scripts so the browser runs them; iframes get their src back.
 */

const BLOCKED_ATTR = 'data-kockica-blocked';
const BLOCKED_TYPE = 'text/plain';

export interface AutoBlockOptions {
  /** Is this category allowed right now? */
  isAllowed: (category: string) => boolean;
  /** Should this tracker be blocked at all (false for GTM, and for Google tags in `googleMode: 'advanced'`)? */
  shouldBlock: (tracker: Tracker) => boolean;
  /** Called the first time a tracker is seen on the page. */
  onDetect?: (tracker: Tracker) => void;
  /** Called when an iframe was blocked after start-up (so placeholders can be drawn). */
  onIframeBlocked?: () => void;
}

export interface AutoBlocker {
  detected: () => TrackerId[];
  /** Run every blocked script / iframe whose category is now accepted. */
  release: (accepted: readonly string[]) => void;
  stop: () => void;
}

interface BlockedScript {
  el: HTMLScriptElement;
  tracker: Tracker;
  originalType: string | null;
}

let active: AutoBlocker | null = null;

export function startAutoBlocking(options: AutoBlockOptions): AutoBlocker {
  if (active) return active;

  const detected = new Set<TrackerId>();
  const blocked: BlockedScript[] = [];
  const doc = document;

  const see = (tracker: Tracker): void => {
    if (detected.has(tracker.id)) return;
    detected.add(tracker.id);
    options.onDetect?.(tracker);
  };

  const mustBlock = (tracker: Tracker): boolean => options.shouldBlock(tracker) && !options.isAllowed(tracker.category);

  const blockScript = (el: HTMLScriptElement, tracker: Tracker): void => {
    if (el.hasAttribute(BLOCKED_ATTR)) return;
    blocked.push({ el, tracker, originalType: el.getAttribute('type') });
    el.setAttribute(BLOCKED_ATTR, tracker.id);
    el.setAttribute('type', BLOCKED_TYPE);
  };

  const inspectScript = (el: HTMLScriptElement): void => {
    const src = el.getAttribute('src');
    if (!src) return;
    const tracker = matchScript(src);
    if (!tracker) return;
    see(tracker);
    if (mustBlock(tracker)) blockScript(el, tracker);
  };

  const inspectIframe = (el: HTMLIFrameElement, late: boolean): void => {
    const src = el.getAttribute('src');
    if (!src || el.hasAttribute('data-kockica-category')) return;
    const tracker = matchIframe(src);
    if (!tracker) return;
    see(tracker);
    if (!mustBlock(tracker)) return;
    // An iframe starts navigating the moment it is inserted, before any script can react, and removing src does not
    // unload it. Swapping in a fresh, never-navigated copy aborts that load so the content can never render.
    const target = el.isConnected ? (el.cloneNode(false) as HTMLIFrameElement) : el;
    target.removeAttribute('src');
    target.setAttribute('data-src', src);
    target.setAttribute('data-kockica-category', tracker.category);
    if (target !== el) el.replaceWith(target);
    if (late) options.onIframeBlocked?.();
  };

  const inspect = (node: Node, late: boolean): void => {
    if (!(node instanceof Element)) return;
    if (node instanceof HTMLScriptElement) inspectScript(node);
    else if (node instanceof HTMLIFrameElement) inspectIframe(node, late);
    else {
      node.querySelectorAll?.('script[src]').forEach((s) => inspectScript(s as HTMLScriptElement));
      node.querySelectorAll?.('iframe[src]').forEach((f) => inspectIframe(f as HTMLIFrameElement, late));
    }
  };

  // Anything already in the document (scripts above Kockica cannot be stopped any more, but are still detected).
  doc.querySelectorAll('script[src]').forEach((s) => {
    const tracker = matchScript(s.getAttribute('src') ?? '');
    if (tracker) see(tracker);
  });
  doc.querySelectorAll('iframe[src]').forEach((f) => inspectIframe(f as HTMLIFrameElement, false));

  let late = false;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) m.addedNodes.forEach((n) => inspect(n, late));
  });
  observer.observe(doc.documentElement, { childList: true, subtree: true });
  const markLate = (): void => {
    late = true;
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', markLate, { once: true });
  else markLate();

  // Firefox fires this before running a parser-inserted script; a second safety net.
  const beforeExec = (event: Event): void => {
    if ((event.target as Element | null)?.getAttribute?.(BLOCKED_ATTR)) event.preventDefault();
  };
  doc.addEventListener('beforescriptexecute', beforeExec, true);

  // Scripts created from JavaScript: intercept src / type on the element itself.
  const originalCreate = doc.createElement;
  const srcDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
  const typeDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'type');
  const originalSetAttribute = Element.prototype.setAttribute;

  const patchScript = (el: HTMLScriptElement): void => {
    if (!srcDesc?.set || !srcDesc.get || !typeDesc?.set || !typeDesc.get) return;
    const setSrc = srcDesc.set;
    const getSrc = srcDesc.get;
    const setType = typeDesc.set;
    const getType = typeDesc.get;
    const afterSrc = (): void => {
      const tracker = matchScript(getSrc.call(el));
      if (!tracker) return;
      see(tracker);
      if (mustBlock(tracker)) blockScript(el, tracker);
    };
    Object.defineProperty(el, 'src', {
      configurable: true,
      get: () => getSrc.call(el),
      set: (value: string) => {
        setSrc.call(el, value);
        afterSrc();
      },
    });
    Object.defineProperty(el, 'type', {
      configurable: true,
      get: () => getType.call(el),
      set: (value: string) => {
        const entry = blocked.find((b) => b.el === el);
        if (entry) entry.originalType = value; // keep it blocked; remember what to restore
        else setType.call(el, value);
      },
    });
    Object.defineProperty(el, 'setAttribute', {
      configurable: true,
      value: (name: string, value: string) => {
        const lower = name.toLowerCase();
        if (lower === 'type' && el.hasAttribute(BLOCKED_ATTR) && value !== BLOCKED_TYPE) {
          const entry = blocked.find((b) => b.el === el);
          if (entry) entry.originalType = value;
          return;
        }
        originalSetAttribute.call(el, name, value);
        if (lower === 'src') afterSrc();
      },
    });
  };

  doc.createElement = function createElement(this: Document, tagName: string, opts?: ElementCreationOptions) {
    const el = originalCreate.call(this, tagName, opts);
    if (el instanceof HTMLScriptElement) patchScript(el);
    return el;
  } as typeof doc.createElement;

  const release = (accepted: readonly string[]): void => {
    for (let i = blocked.length - 1; i >= 0; i--) {
      const entry = blocked[i];
      if (!entry || !accepted.includes(entry.tracker.category)) continue;
      blocked.splice(i, 1);
      const { el, originalType } = entry;
      el.removeAttribute(BLOCKED_ATTR);
      if (!el.isConnected) {
        // Not inserted yet: restoring the type is enough, it will run when the snippet inserts it.
        if (originalType) originalSetAttribute.call(el, 'type', originalType);
        else el.removeAttribute('type');
        continue;
      }
      // A connected script never runs again after a type change; replace it with a fresh one.
      const fresh = originalCreate.call(doc, 'script') as HTMLScriptElement;
      for (const attr of Array.from(el.attributes)) {
        if (attr.name === 'type' || attr.name === BLOCKED_ATTR) continue;
        originalSetAttribute.call(fresh, attr.name, attr.value);
      }
      if (originalType) originalSetAttribute.call(fresh, 'type', originalType);
      if (el.text) fresh.text = el.text;
      el.replaceWith(fresh);
    }
  };

  const stop = (): void => {
    observer.disconnect();
    doc.removeEventListener('beforescriptexecute', beforeExec, true);
    doc.createElement = originalCreate;
    active = null;
  };

  active = { detected: () => [...detected], release, stop };
  return active;
}
